import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PublicHeader } from "@/components/PublicHeader";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";

/**
 * TeamPage — /team
 *
 * PUBLIC page (no RegistrationGate) that lists everyone who serves at
 * Iglesia Nueva Cancion, grouped by their team (Pastor, Worship team,
 * Church council, ...). Groups + members are all admin-managed.
 *
 * Layout: mobile-first list per group, with a big round portrait,
 * name + role, and an optional bio paragraph.
 */
export default function TeamPage() {
  const { t, lang } = useI18n();
  const { member } = useMember();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);

  // If the visitor hasn't registered yet, /social is behind the RegistrationGate
  // and would bounce them straight back to /register. Send them there directly
  // with a clearer label so the flow feels intentional.
  const backTo = member ? "/social" : "/register";
  const backLabel = member
    ? (t?.social?.title || (lang === "es" ? "Social" : "Social"))
    : (t?.register?.backToRegister || (lang === "es" ? "Volver al registro" : "Back to register"));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/team");
        if (!cancelled) setGroups(data?.groups || []);
      } catch (err) {
        console.error("TeamPage: failed to load", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isEmpty = !loading && groups.length === 0;
  const isAllEmpty = !loading && groups.length > 0
    && groups.every((g) => !(g.members || []).length);

  return (
    <div>
      <PublicHeader />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-5"
      >
        <div className="px-1">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1 h-8 rounded-full text-muted-foreground"
            data-testid="team-back-link"
          >
            <Link to={backTo}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              {backLabel}
            </Link>
          </Button>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h1 className="font-serif-display text-2xl font-semibold tracking-tight" data-testid="team-title">
              {lang === "es" ? "Conoce Nuestra Iglesia" : "Meet Our Church"}
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {lang === "es"
              ? "Estas son las personas que sirven a la Iglesia Nueva Cancion."
              : "These are the people who serve at Iglesia Nueva Cancion."}
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-full rounded-2xl" />
            ))}
          </div>
        ) : isEmpty || isAllEmpty ? (
          <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
            <CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="team-empty">
              {lang === "es"
                ? "Pronto compartiremos las personas que sirven en la iglesia."
                : "We will soon share the people who serve at the church."}
            </CardContent>
          </Card>
        ) : (
          groups
            .filter((g) => (g.members || []).length > 0)
            .map((g) => (
              <section key={g.id} data-testid={`team-group-${g.id}`} className="space-y-3">
                <div className="px-1">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-primary">
                    {lang === "es" ? "Grupo" : "Group"}
                  </p>
                  <h2 className="font-serif-display text-xl font-semibold leading-tight" data-testid={`team-group-name-${g.id}`}>
                    {lang === "es" ? g.name_es : g.name_en}
                  </h2>
                </div>
                <div className="space-y-3">
                  {(g.members || []).map((m) => {
                    const role = lang === "es"
                      ? (m.role_es || m.role_en)
                      : (m.role_en || m.role_es);
                    const bio = lang === "es"
                      ? (m.bio_es || m.bio_en)
                      : (m.bio_en || m.bio_es);
                    return (
                      <Card
                        key={m.id}
                        className="rounded-2xl ring-1 ring-black/5 shadow-sm overflow-hidden"
                        data-testid={`team-member-${m.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <img
                              src={m.image_url}
                              alt={m.full_name}
                              className="h-20 w-20 shrink-0 rounded-full object-cover ring-1 ring-black/5"
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
                              data-testid={`team-member-photo-${m.id}`}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-serif-display text-lg font-semibold leading-tight break-words" data-testid={`team-member-name-${m.id}`}>
                                {m.full_name}
                              </p>
                              {role && (
                                <p className="text-xs uppercase tracking-wider text-primary/80 mt-0.5">{role}</p>
                              )}
                              {bio && (
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                                  {bio}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            ))
        )}

        <div className="h-2" />
      </motion.div>
    </div>
  );
}
